import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { toast } from 'react-toastify';
import styles from './PropertyProposalSign.module.css';

/**
 * Property Proposal Sign Page
 * Laravel equivalent: SignedPropertyProposalController@showSignPage
 * View: properties/reports/property_proposal/sign.blade.php
 */
const PropertyProposalSign = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureType, setSignatureType] = useState('drawn'); // drawn, typed, uploaded
  const [signatureName, setSignatureName] = useState('');
  const [typedSignature, setTypedSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    loadProposalData();
  }, [proposalId]);

  useEffect(() => {
    // Initialize canvas for signature drawing
    if (canvasRef.current && signatureType === 'drawn') {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = 200;

      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.strokeStyle = 'black';
      context.lineWidth = 2;
      contextRef.current = context;
    }
  }, [signatureType]);

  const loadProposalData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/propertyproposals/${proposalId}/sign`);

      if (response.data.success) {
        setProposal(response.data.data.proposal);
      } else {
        toast.error(response.data.message || 'Failed to load proposal');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error loading proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to load proposal for signing');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing handlers
  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;

    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSubmitSignature = async (e) => {
    e.preventDefault();

    // Validation
    if (!signatureName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    let signatureData = '';

    if (signatureType === 'drawn') {
      // Get canvas data
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

      // Check if canvas is empty
      const isCanvasEmpty = !imageData.data.some(channel => channel !== 0);
      if (isCanvasEmpty) {
        toast.error('Please draw your signature');
        return;
      }

      signatureData = canvas.toDataURL();
    } else if (signatureType === 'typed') {
      if (!typedSignature.trim()) {
        toast.error('Please type your signature');
        return;
      }
      signatureData = typedSignature;
    }

    try {
      setSigning(true);

      const response = await api.post(`/propertyproposals/${proposalId}/sign`, {
        signed_client_contact_name: signatureName,
        contact_signature: signatureData,
        date_sign: new Date().toISOString()
      });

      if (response.data.success) {
        toast.success('Property Proposal signed successfully!');
        // Navigate back to property show page
        navigate(`/properties/${proposal.property_id}`);
      } else {
        toast.error(response.data.message || 'Failed to sign proposal');
      }
    } catch (error) {
      console.error('Error signing proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to sign proposal');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">
          <i className="fa fa-spinner fa-spin fa-3x"></i>
          <p>Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="container">
        <div className="alert alert-danger">
          <strong>Error:</strong> Proposal not found
        </div>
      </div>
    );
  }

  return (
    <div className="container sign-proposal-container">
      <div className="row">
        <div className="col-md-8 col-md-offset-2">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h3>Sign Property Proposal</h3>
            </div>
            <div className="panel-body">
              <div className="proposal-info">
                <h4>Proposal Details</h4>
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>Property ID:</strong></td>
                      <td>{proposal.property_id}</td>
                    </tr>
                    <tr>
                      <td><strong>Client:</strong></td>
                      <td>{proposal.client}</td>
                    </tr>
                    <tr>
                      <td><strong>Proposal Date:</strong></td>
                      <td>{proposal.proposal_date ? new Date(proposal.proposal_date).toLocaleDateString('en-GB') : 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Account Manager:</strong></td>
                      <td>{proposal.account_manager}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <hr />

              <form onSubmit={handleSubmitSignature}>
                <div className="form-group">
                  <label htmlFor="signatureName">Full Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="signatureName"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Signature Type</label>
                  <div className="btn-group btn-group-justified signature-type-selector">
                    <div className="btn-group">
                      <button
                        type="button"
                        className={`btn ${signatureType === 'drawn' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => setSignatureType('drawn')}
                      >
                        <i className="fa fa-pencil"></i> Draw
                      </button>
                    </div>
                    <div className="btn-group">
                      <button
                        type="button"
                        className={`btn ${signatureType === 'typed' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => setSignatureType('typed')}
                      >
                        <i className="fa fa-keyboard-o"></i> Type
                      </button>
                    </div>
                  </div>
                </div>

                {signatureType === 'drawn' && (
                  <div className="form-group">
                    <label>Draw Your Signature <span className="text-danger">*</span></label>
                    <div className="signature-canvas-container">
                      <canvas
                        ref={canvasRef}
                        className="signature-canvas"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={clearCanvas}
                      style={{ marginTop: '10px' }}
                    >
                      <i className="fa fa-eraser"></i> Clear Signature
                    </button>
                  </div>
                )}

                {signatureType === 'typed' && (
                  <div className="form-group">
                    <label htmlFor="typedSignature">Type Your Signature <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control signature-typed"
                      id="typedSignature"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      placeholder="Type your full name as signature"
                      required
                      style={{ fontFamily: 'cursive', fontSize: '24px' }}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Date of Signature</label>
                  <input
                    type="text"
                    className="form-control"
                    value={new Date().toLocaleDateString('en-GB')}
                    disabled
                  />
                </div>

                <div className="alert alert-info">
                  <i className="fa fa-info-circle"></i> By signing this proposal, you acknowledge that you have read and agree to the terms and conditions outlined in this property proposal.
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => navigate(-1)}
                    disabled={signing}
                  >
                    <i className="fa fa-times"></i> Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={signing}
                  >
                    {signing ? (
                      <>
                        <i className="fa fa-spinner fa-spin"></i> Signing...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-check"></i> Sign Proposal
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyProposalSign;
